import React, { useEffect, useState, useCallback, useMemo, createContext, useContext } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EntityCategoriesContext = createContext({ categories: [], loading: false, reload: () => {} });

/**
 * Provider that fetches entity categories once per mount and exposes them
 * via useEntityCategories(). Cached in module state so navigating between
 * the customer / lead / admin pages doesn't refetch.
 */
export function EntityCategoriesProvider({ children }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/entity-categories`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setCategories(data.categories || []);
    } catch (e) {
      // Silent — the multi-select just renders empty.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const value = useMemo(() => ({ categories, loading, reload: load }), [categories, loading, load]);
  return <EntityCategoriesContext.Provider value={value}>{children}</EntityCategoriesContext.Provider>;
}

export function useEntityCategories() {
  return useContext(EntityCategoriesContext);
}

/**
 * Compact multi-select checkbox row for use inside Customer / Lead forms.
 * Props: value (List<string>), onChange (List<string> => void), disabled
 */
export function CategoryCheckboxGroup({ value = [], onChange, disabled = false, className = '' }) {
  const { categories, loading } = useEntityCategories();
  const selected = new Set(value || []);

  const toggle = (id) => {
    if (disabled) return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange && onChange(Array.from(next));
  };

  if (loading && categories.length === 0) {
    return <div className={`text-xs text-slate-500 ${className}`}>Loading categories…</div>;
  }
  if (categories.length === 0) {
    return <div className={`text-xs text-slate-400 ${className}`}>No categories available.</div>;
  }

  return (
    <div className={`flex flex-wrap gap-2 ${className}`} data-testid="category-checkbox-group">
      {categories.map((c) => {
        const active = selected.has(c.id);
        return (
          <button
            key={c.id}
            type="button"
            disabled={disabled}
            onClick={() => toggle(c.id)}
            data-testid={`category-chip-${c.key}`}
            data-active={active ? '1' : '0'}
            className={
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ' +
              (active
                ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50')
            }
          >
            <span className={
              'inline-block w-3 h-3 rounded border ' +
              (active ? 'bg-white border-white' : 'border-slate-400')
            }>
              {active ? (
                <svg viewBox="0 0 12 12" className="w-3 h-3 text-indigo-600">
                  <path d="M2 6 L5 9 L10 3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : null}
            </span>
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Compact "filter by category" chip row for use above a list of
 * customers/leads. Multi-select — clicking a chip toggles it; if no chip
 * is active the list is unfiltered.
 *
 * Props: value (List<string>), onChange (List<string> => void),
 * counts (optional { [categoryId]: number } to show record counts per chip)
 */
export function CategoryFilterChips({ value = [], onChange, counts = {}, className = '' }) {
  const { categories } = useEntityCategories();
  if (!categories || categories.length === 0) return null;
  const selected = new Set(value || []);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange && onChange(Array.from(next));
  };
  const clear = () => onChange && onChange([]);

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid="category-filter-chips">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mr-1">Category:</span>
      {categories.map((c) => {
        const active = selected.has(c.id);
        const count = counts[c.id];
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => toggle(c.id)}
            data-testid={`filter-chip-${c.key}`}
            data-active={active ? '1' : '0'}
            className={
              'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border transition ' +
              (active
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50')
            }
          >
            {c.label}
            {typeof count === 'number' ? (
              <span className={`ml-1 text-[10px] px-1 rounded ${active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>{count}</span>
            ) : null}
          </button>
        );
      })}
      {selected.size > 0 ? (
        <button
          type="button"
          onClick={clear}
          className="text-[11px] text-slate-500 hover:text-slate-700 underline ml-1"
          data-testid="filter-chip-clear"
        >
          clear
        </button>
      ) : null}
    </div>
  );
}
export function CategoryBadges({ categoryIds = [], size = 'xs' }) {
  const { categories } = useEntityCategories();
  if (!categoryIds || categoryIds.length === 0) return null;
  const byId = new Map(categories.map((c) => [c.id, c]));
  const sizeCls = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-[10px] px-1.5 py-0.5';
  return (
    <span className="inline-flex flex-wrap gap-1">
      {categoryIds.map((id) => {
        const c = byId.get(id);
        if (!c) return null;
        return (
          <span
            key={id}
            className={`${sizeCls} rounded-full bg-indigo-50 text-indigo-800 border border-indigo-200 font-medium`}
            data-testid={`category-badge-${c.key}`}
          >
            {c.label}
          </span>
        );
      })}
    </span>
  );
}
